/**
 * Колокольчик с внутренней лентой.
 *
 * Внешних каналов нет — ни почты, ни мессенджеров. Платформа размещается на
 * собственном сервере, и любой внешний канал означал бы, что названия задач
 * и проектов уходят наружу.
 *
 * Счётчик считает только то, что требует действия: просрочку и приближающийся
 * срок. Комментарий и вынесенное на проверку в ленте есть, но цифру на
 * колокольчике не поднимают — иначе она перестанет что-либо значить.
 *
 * Прочитанность отмечается на устройстве: уведомления не хранятся, а
 * выводятся из состояния проекта, поэтому «прочитано» — это свойство
 * смотрящего, а не события.
 */

import {
  BellIcon,
  CheckIcon,
  ClockAlertIcon,
  MessageSquareIcon,
  TimerIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { NotificationDto } from '@project-os/contracts';
import { useNotifications } from '@/api/notifications';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatRelative, pluralRu } from '@/lib/format';
import { useLocalSetting } from '@/lib/use-local-setting';
import { cn } from '@/lib/utils';

const ICONS: Record<NotificationDto['kind'], typeof BellIcon> = {
  overdue: TriangleAlertIcon,
  'due-soon': TimerIcon,
  'review-requested': ClockAlertIcon,
  comment: MessageSquareIcon,
};

const TONE_CLASS: Record<NotificationDto['tone'], string> = {
  danger: 'text-tone-danger',
  warning: 'text-tone-warning',
  neutral: 'text-muted-foreground',
};

export function NotificationBell({
  projectId,
  onOpenItem,
}: {
  projectId: string;
  onOpenItem: (itemId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data } = useNotifications(projectId);

  /**
   * Момент последнего просмотра ленты.
   *
   * Хранится строкой, а не набором прочитанных идентификаторов: события
   * пересчитываются каждый раз, и их идентификаторы не переживают изменение
   * срока. Отметка времени переживает.
   */
  const [seenAt, setSeenAt] = useLocalSetting<string | null>(`seen-${projectId}`, null);

  const unseen = useMemo(() => {
    if (!data) return 0;
    if (!seenAt) return data.count;
    // Считаем только требующее действия и появившееся после просмотра.
    return data.entries.filter((entry) => entry.tone !== 'neutral' && entry.at > seenAt).length;
  }, [data, seenAt]);

  const entries = data?.entries ?? [];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Отметка ставится при открытии, а не при закрытии: человек уже
        // увидел ленту, и держать счётчик до закрытия окна нечестно.
        if (next) setSeenAt(new Date().toISOString());
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8"
          aria-label={
            unseen > 0
              ? `Уведомления, ${pluralRu(unseen, ['новое', 'новых', 'новых'])}`
              : 'Уведомления'
          }
        >
          <BellIcon className="size-4" />
          {unseen > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-tone-danger px-1 text-[9px] font-semibold text-primary-foreground tabular-nums">
              {unseen > 9 ? '9+' : unseen}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2">
          <span className="text-sm font-medium">Уведомления</span>
          <span className="text-2xs text-muted-foreground">за неделю</span>
          <div className="flex-1" />
          {data && data.count > 0 ? (
            <span className="text-2xs text-tone-danger">
              {pluralRu(data.count, ['требует внимания', 'требуют внимания', 'требуют внимания'])}
            </span>
          ) : null}
        </div>

        {entries.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <CheckIcon className="mx-auto mb-2 size-6 text-tone-success" />
            <p className="text-sm">Ничего не требует внимания</p>
            <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
              Здесь появятся просроченные задачи, приближающиеся сроки, материалы на проверке и
              новые комментарии. Внешних писем платформа не шлёт.
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-96">
            <ul className="divide-y divide-border/40">
              {entries.map((entry) => {
                const Icon = ICONS[entry.kind];
                return (
                  <li key={entry.id}>
                    <button
                      type="button"
                      className="row-hover flex w-full items-start gap-2.5 px-3 py-2 text-left"
                      onClick={() => {
                        if (entry.itemId) {
                          onOpenItem(entry.itemId);
                          setOpen(false);
                        }
                      }}
                    >
                      <Icon className={cn('mt-0.5 size-3.5 shrink-0', TONE_CLASS[entry.tone])} />

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm">{entry.title}</span>
                        <span
                          className={cn(
                            'mt-0.5 block truncate text-2xs',
                            entry.tone === 'neutral'
                              ? 'text-muted-foreground'
                              : TONE_CLASS[entry.tone],
                          )}
                        >
                          {entry.detail}
                        </span>
                      </span>

                      <span className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-2xs text-muted-foreground">
                          {formatRelative(entry.at)}
                        </span>
                        {entry.actor ? (
                          <UserAvatar user={entry.actor} size="xs" title={entry.actor.name} />
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
